/**
 * What a plan or campaign includes (issue #113): the usages declared inside it,
 * each typed by a case or plan def that lives in a package. Including one is
 * declaring a usage - `verification x : Def :> cases;` - so the same case can
 * sit in several plans. Removing one deletes the usage, never the def.
 */

import { useCallback, useEffect, useState } from 'react'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ElementPicker from '../common/ElementPicker'
import { PAGE_LIMIT, api } from '../../api'
import { useCanWrite, useSchema } from '../../state/AppData'
import type { VerificationRow } from '../../schema'
import { MONOSPACE } from '../../theme'

/** What each owner may include. */
const MEMBER: Record<'plan' | 'campaign', 'case' | 'plan'> = { plan: 'case', campaign: 'plan' }

/** The usages declared inside one owner. */
export function includesOf(rows: VerificationRow[], owner: { id: string; qualified_name: string }) {
  return rows.filter(
    (row) =>
      !row.is_definition &&
      row.owner !== null &&
      row.owner !== undefined &&
      (row.owner === owner.id || row.owner === owner.qualified_name),
  )
}

export default function VerificationIncludes({
  owner,
}: {
  owner: { id: string; qualified_name: string; kind: 'plan' | 'campaign' }
}) {
  const schema = useSchema()
  const { allowed, reason } = useCanWrite()
  const member = MEMBER[owner.kind]

  const [rows, setRows] = useState<VerificationRow[] | null>(null)
  const [failure, setFailure] = useState('')
  const [chosen, setChosen] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    api
      .verification({ limit: PAGE_LIMIT })
      .then((page) => setRows(page.items))
      .catch((error: Error) => setFailure(error.message))
  }, [])

  useEffect(() => {
    load()
  }, [load, owner.id])

  const act = async (work: () => Promise<unknown>) => {
    setBusy(true)
    setFailure('')
    try {
      await work()
      setChosen('')
      load()
    } catch (error) {
      setFailure((error as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const included = rows ? includesOf(rows, owner) : []

  return (
    <Box>
      <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
        Includes
      </Typography>
      {failure && <Alert severity="error">{failure}</Alert>}

      {rows && included.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          This {owner.kind} includes no existing {member}s.
        </Typography>
      )}
      {included.length > 0 && (
        <List dense aria-label="Includes">
          {included.map((row) => (
            <ListItem
              key={String(row.qualified_name)}
              secondaryAction={
                <Tooltip title={allowed ? 'Remove from here; the definition stays' : reason}>
                  <span>
                    <IconButton
                      edge="end"
                      aria-label={`Remove ${row.name}`}
                      disabled={!allowed || busy}
                      onClick={() => void act(() => api.removeInclusion(owner.id, String(row.name)))}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              }
            >
              <ListItemText
                primary={row.name}
                secondary={<span style={{ fontFamily: MONOSPACE }}>{String(row.type_name ?? '')}</span>}
              />
            </ListItem>
          ))}
        </List>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1 }}>
          <ElementPicker
            key={member}
            schema={schema}
            kind="verification"
            where={(row) => {
              const entry = row as VerificationRow
              return entry.verification_kind === member && Boolean(entry.is_definition)
            }}
            valueFor={(row) => String(row.qualified_name)}
            value={chosen || null}
            onChange={setChosen}
            label={`Include existing ${member}…`}
            disabled={!allowed}
          />
        </Box>
        <Tooltip title={allowed ? '' : reason} describeChild>
          <span>
            <Button
              disabled={!allowed || !chosen || busy}
              onClick={() => void act(() => api.includeVerification(owner.id, chosen))}
            >
              Include
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  )
}
